import React, { useContext, useEffect, useRef, useState } from "react"
import { Button } from "react-bootstrap"
import { ErrorBoundary } from 'react-error-boundary'
import { toast } from "react-toastify"
import { Column, Icon, Row, Select } from '../../../components'
import Grid from '../../../components/components/Grid/Grid'
import AppContext from '../../../shared/components/AppContext'
import { FallbackComponent } from '../../../shared/components/FallbackComponent'
import { useConfirmationModalContext } from '../../../shared/components/ModalConfirmationContext'
import * as constant from '../../../shared/util/constant'
import { AgencySetupLabels as labels } from '../../../shared/util/label'
import AgencyMembershipModel from "../../model/AgencyMembershipModel"
import ScreenElementAgencyModel from "../../model/ScreenElementAgencyModel"
import AgencySetupService from "./AgencySetupService"

interface AgencyGridProps {
    groupId: string;
    data: ScreenElementAgencyModel[];
    allUniqueAgencies: any[];
    listOfAllAgencies: any[];
    canDelete?: boolean;
}


const AgencySetupGrid: React.FunctionComponent<AgencyGridProps> = ({
    groupId,
    data,
    allUniqueAgencies,
    listOfAllAgencies,
    canDelete = true,
}) => {
    //const [listOfNewAgencies, setListOfNewAgencies] = useState([]);  //Save the id of newly added agencies
    const [gridData, setAllAgencyGrid] = useState(data);
    const agencyGroupsContext = useContext(AppContext);
    const modalContext = useConfirmationModalContext();
    const [selectedOption, setSelectedOption] = useState('');
    const [allUniqueAgency, setAllUniqueAgencies] = useState(allUniqueAgencies);
    const [disableAddButton, setDisableAddButton] = useState(false);
    const service = new AgencySetupService();
    const divToScroll = useRef(null)

    useEffect(() => {
        initializeGroupList();
        setAllUniqueAgencies(allUniqueAgencies);
        setDisableAddButton(false);        
    }, [data]);

    const initializeGroupList = async () => {
        if (data) {
            let response = data?.map((item) => {
                return {
                    agencyId: item.agencyId,
                    agencyName: item.agencyName,
                }
            })
            setAllAgencyGrid(response);
        }
    }

    const onFieldChange = async (value: string) => {
        if (value != undefined || value != "") {
            setSelectedOption(value.toString());
            const membership: AgencyMembershipModel = {
                groupId: groupId,
                agencyId: value.toString()
            };

            service.saveAgencyMembership(membership)
                .then((response) => {
                    if (response) {
                        toast.success("Group Name saved successfully", {
                            position: toast.POSITION.TOP_RIGHT,
                            autoClose: constant.AUTO_CLOSE
                        });
                        let selectedData = listOfAllAgencies.filter(item => item.value == value.toString())

                        //Updated Grid data after adding new agency
                        let updatedGridData = gridData.filter(item => item.agencyName != null)
                        const newRow: ScreenElementAgencyModel = {
                            agencyId: selectedData[0].value,
                            agencyName: selectedData[0].label,
                        }
                        updatedGridData.push(newRow);

                        setAllAgencyGrid([...updatedGridData].sort((a, b) => a.agencyName.localeCompare(b.agencyName)));

                        let specificAgecny = allUniqueAgency.filter(item => item.value != value.toString());
                        setAllUniqueAgencies([...specificAgecny]);
                        setDisableAddButton(false);

                    }
                    else {
                        toast.error("Unable to save the Group Name", {
                            position: toast.POSITION.TOP_RIGHT,
                            autoClose: constant.AUTO_CLOSE
                        });
                    }
                })
                .catch(e => {
                    console.log(e);
                });
        }
    }

    const deleteRow = async (rowIndex: number, agencyName: string) => {
        if (agencyName != "") {
            const result = await modalContext.showConfirmation(`Are you sure you want to delete the Agency: ${agencyName}?`);
            if (result) {
                const membership: AgencyMembershipModel = {
                    groupId: groupId,
                    agencyId: rowIndex.toString()
                };

                service.deleteAgencyMembership(membership)
                    .then((response) => {
                        if (response) {
                            toast.success("Agency successfully deleted", {
                                position: toast.POSITION.TOP_RIGHT,
                                autoClose: constant.AUTO_CLOSE
                            });
                            let selectedData = gridData.filter(item => item.agencyId != rowIndex.toString())
                            setAllAgencyGrid([...selectedData]);
                            let addData = listOfAllAgencies.filter(item => item.value == rowIndex.toString())

                            const addAgency: any = {
                                label: addData[0].label,
                                value: addData[0].value,
                            }
                            setAllUniqueAgencies([...allUniqueAgency, addAgency]);
                            let newDDLCheck = gridData.filter(item => item.agencyName == null)
                            if (disableAddButton && newDDLCheck.length<=0) {
                                setDisableAddButton(false);
                            }
                        }
                        else {
                            toast.error("Unable to delete the agency", {
                                position: toast.POSITION.TOP_RIGHT,
                                autoClose: constant.AUTO_CLOSE
                            });
                        }
                    })
                    .catch(e => {
                        console.log(e);
                        setDisableAddButton(false);
                    });
            }
        }
    }
    const addNewRow = () => {
        setDisableAddButton(true);
        const newRow: ScreenElementAgencyModel = {
            agencyId: constant.DEFAULT_GUID,
            agencyName: null,
        };
        
        setAllAgencyGrid(gridData => {
            const newArray = [...gridData];
            newArray.unshift(newRow);
            return newArray;
        });
    }
    const columns: any[] = [
        {
            Header: "",
            accessor: "delete",
            Cell: row => {
                return (
                    <div className='delete-container'>
                        {row.row?.original.agencyId != constant.DEFAULT_GUID &&
                            <Icon
                                icon={"trash"}
                                className={`grid-icon ${disableAddButton || !canDelete ? ' disabled' : ''}`}
                                onClick={() => canDelete ? deleteRow(row.row.original.agencyId, row.row.original.agencyName) : undefined}
                            ></Icon>
                        }
                    </div>

                )
            },
            width: 100,
            cellClassName: "",
            headerClassName: ""
        },
        {
            Header: labels.AGENCY,
            accessor: "screen",
            Cell: row => {
                return (
                    <div>
                        {
                            (() => {
                                const data = row.row?.original;
                                switch (true) {
                                    case (data?.agencyName != null):
                                        return <span id={row.row.original.agencyName} title={row.row.original.agencyName}>{row.row.original.agencyName}</span>;
                                    default:
                                        return (<><Select
                                            label={<>{labels.PLEASE_SELECT} <span className="text-danger"> *</span></>}
                                            id={'agencyGrid'}
                                            placeholder={labels.PLEASE_SELECT}
                                            options={allUniqueAgency !== undefined ? allUniqueAgency : []}
                                            onChange={(values) => onFieldChange(values[0] as string)}
                                            multiple={false} />
                                        </>);
                                }
                            })()
                        }
                    </div>
                )
            },
            width: 735,
            cellClassName: "",
            headerClassName: ""
        }];

    return (
        <>
            <Row>
                <Column lg={8} className=''>
                    <div className='grid-container card table-container-agency'>
                        <ErrorBoundary FallbackComponent={FallbackComponent}>
                            <Grid
                                ref={divToScroll}
                                data={gridData}
                                id={'AgencyDataGrid'}
                                columns={columns}
                                hiddenColumns={["selection"]}
                                disableSorting={true}
                                paginationDisabled={true}
                                showFilters={false}
                            />
                        </ErrorBoundary>

                    </div>
                </Column>
                <Column lg={1} className=''>
                    <div className='d-flex justify-content-between align-items-center mb-1'>
                        <div className='add-container'>{groupId &&
                            <Button
                                disabled={disableAddButton}
                                onClick={addNewRow}
                                className='grid-icon'
                                outlined={true}
                                size='small'>
                                <Icon icon="add" className="me-1" />
                            </Button>}
                        </div>
                    </div>
                </Column>
            </Row>
        </>
    );
};

export default AgencySetupGrid
